import {
  AutoPaymentCollectionTimingEnum,
  getBillGroupCCEmailsResSchema,
  IBillGroupCCEmailsResSchema,
  IBillGroupResp,
  IInvoiceRespSchema,
} from '@monetize/types/app';
import { EmailStringToEmailListSchema } from '@monetize/types/common';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { MdCreditCard } from 'react-icons/md';
import { useSendEmail } from '../../api/accountsService';
import { handleApiErrorToast } from '../../api/axios';
import { useGetById } from '../../api/queryUtils';
import {
  MButton,
  MCenterModal,
  MFlex,
  MIcon,
  MSkeleton,
  MStack,
  MText,
} from '../../components/Monetize';
import MEmailTextarea from '../../components/Monetize/MEmailTextarea';
import { useToast } from '../../services/toast';

interface IEmailInvoiceModalProps {
  invoice: IInvoiceRespSchema;
  isOpen: boolean;
  onClose: () => void;
  refreshData: () => void;
}

export const EmailInvoiceModal = ({
  invoice: { id: invoiceId, billingContact, billGroupId },
  isOpen,
  onClose,
  refreshData,
}: IEmailInvoiceModalProps) => {
  const { addToast } = useToast();
  const { mutateAsync: doSendEmail } = useSendEmail();
  const [loading, setLoading] = useState<boolean>(false);

  const { data: billGroups, isLoading } = useGetById<IBillGroupResp>(
    'billGroups',
    billGroupId,
    {
      enabled: isOpen && !!billGroupId,
      meta: { showErrorToast: true },
      refetchOnWindowFocus: false,
    },
  );

  const { handleSubmit, control, reset } = useForm<IBillGroupCCEmailsResSchema>(
    {
      mode: 'onChange',
      resolver: getBillGroupCCEmailsResSchema(),
      defaultValues: {
        strCcEmails: null,
      },
    },
  );

  useEffect(() => {
    if (billGroups) {
      reset({
        strCcEmails: billGroups.ccEmails?.join(',') ?? null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billGroups]);

  const showAutoPay =
    billGroups?.autoPaymentCollectionTiming ===
      AutoPaymentCollectionTimingEnum.INVOICE_SEND &&
    billGroups?.collectInvoiceBalanceAutomatically === true &&
    !!billGroups?.paymentMethodId;

  const onSubmit = async (formData: IBillGroupCCEmailsResSchema) => {
    setLoading(true);
    try {
      const result = EmailStringToEmailListSchema.safeParse(
        formData.strCcEmails,
      );

      const { paymentCollectionAttempted, paymentCollected } =
        await doSendEmail({
          invoiceId,
          overrideCcEmails: result.success ? result.data : null,
        });

      if (paymentCollectionAttempted && !paymentCollected) {
        addToast({
          summary: 'Invoice Sent',
          detail: 'Invoice sent, but payment collection failed.',
          severity: 'warning',
        });
      } else {
        addToast({
          summary: 'Invoice Sent',
          detail: 'The Invoice has been successfully sent.',
          severity: 'success',
        });
      }

      refreshData();
      onClose();
    } catch (err) {
      handleApiErrorToast(err);
    } finally {
      setLoading(false);
    }
  };
  const renderFooter = () => (
    <MStack gap={4} direction="row" align="center" justify="right" flex={1}>
      <MButton variant="cancel" onClick={onClose} minW="auto">
        Cancel
      </MButton>
      <MButton
        form="send-email-form"
        isLoading={loading}
        variant="primary"
        type="submit"
        minW="auto"
      >
        Send
      </MButton>
    </MStack>
  );

  return (
    <MCenterModal
      isOpen={isOpen}
      onClose={onClose}
      modalTitle="Send Invoice through email"
      size="md"
      modalHeaderProps={{
        pb: 2,
      }}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      renderFooter={renderFooter}
    >
      <form id="send-email-form" onSubmit={handleSubmit(onSubmit)}>
        <MFlex direction="column" gap={4} w="full">
          <MText>
            This invoice will be emailed to{' '}
            <MText as="span" color="tPurple.dark" fontWeight="semibold">
              {billingContact.email}
            </MText>{' '}
            The recipient will be able to pay for the invoice through email.
          </MText>
          <MText>
            You can add emails that you would like to send the invoice to
          </MText>
          {isLoading ? (
            <MSkeleton height={14} />
          ) : (
            <MEmailTextarea
              label="Send to additional email addresses"
              tooltip="Use commas or new lines to add multiple email addresses."
              placeholder="Use commas or new lines to add multiple email addresses."
              fieldName="strCcEmails"
              control={control}
            />
          )}
          {showAutoPay && (
            <MFlex alignItems="center" gap={1}>
              <MIcon as={MdCreditCard} boxSize={3} color="tPurple.base" />
              <MText fontSize="xs" color="tPurple.base">
                Auto-payment will be attempted on send
              </MText>
            </MFlex>
          )}
        </MFlex>
      </form>
    </MCenterModal>
  );
};
